/**
 * ClassGrid.jsx — Stacked Scroll Card Deck
 *
 * Animated "Classes And Workshops" headline (whileInView, slides from right).
 *
 * Below the headline: a sticky scroll container where all class cards start
 * stacked in a deck. As the user scrolls, the page "pauses" (sticky budget)
 * while each card peels upward one by one, revealing the next card beneath.
 *
 * Card design:
 *   - Background: #F9F2E9 (same as Dallas Pottery tile)
 *   - Unique earthy border color per class
 *   - Small hand-drawn-style SVG icon per class type
 *   - Titan One title, Raleway 700 body, #9C7A3C accents
 *
 * Props:
 *   onBook {Function} — called with the class object when CTA is clicked
 */
import { useRef, useMemo } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import {
  useClassCards,
  usePublicSessions,
  useTemplateDefaults,
} from '../services/firestore';
import { TEMPLATE_TO_CLASS_ICON } from '../data/defaultTemplates';
import ClassIcon from './icons/ClassIcon';
import { analytics } from '../firebase';
import { logEvent } from 'firebase/analytics';

// ── Card tilt pattern: right, left, straight, right, left, straight ──
const CARD_ROTATE = [4, -4, 0, 4, -4, 0];

// ── Individual card — y driven by scrollYProgress ──────────────
function StackCard({
  c,
  index,
  total,
  scrollYProgress,
  active,
  label,
  onBook,
  cardImage,
  cardIconUrl,
}) {
  const accent = c.blobColor || '#9C7A3C';
  const classId = TEMPLATE_TO_CLASS_ICON[c.templateId] || 1;

  // Each card exits upward during its scroll segment [index/total, (index+1)/total]
  // useTransform clamps: before the segment the card is at 0%, after at -115%
  const y = useTransform(
    scrollYProgress,
    [index / total, (index + 1) / total],
    ['0%', '-115%'],
  );

  const isSingle = total === 1;

  return (
    <motion.div
      style={{
        position: isSingle ? 'relative' : 'absolute',
        ...(isSingle ? { width: '100%', height: '100%' } : { inset: 0 }),
        y: isSingle ? 0 : y,
        rotate: isSingle ? 0 : (CARD_ROTATE[index] ?? 0),
        zIndex: total - index,
        borderRadius: 22,
        background: '#F9F2E9',
        border: `4px solid ${accent}`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding:
          'clamp(28px, 4vh, 44px) clamp(24px, 5vw, 40px) clamp(24px, 3vh, 36px)',
        boxSizing: 'border-box',
        cursor: 'default',
        boxShadow: `0 12px 40px rgba(26, 18, 8, 0.10)`,
      }}
      animate={{ scale: [0.96, 1.04, 0.96] }}
      transition={{
        duration: 2.8,
        repeat: Infinity,
        ease: 'easeInOut',
        delay: index * 0.12,
      }}
      onClick={() => {
        if (active) {
          if (analytics)
            logEvent(analytics, 'select_item', {
              item_name: c.title,
              price: c.price,
            });
          onBook(c);
        }
      }}
    >
      {/* Icon + tag */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 10,
          marginBottom: 'clamp(14px, 2vh, 22px)',
        }}
      >
        <div
          style={{
            width: 52,
            height: 52,
            borderRadius: '50%',
            background: `${accent}18`,
            border: `1.5px solid ${accent}55`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {cardIconUrl ? (
            <img
              src={cardIconUrl}
              alt={c.title}
              style={{ width: 32, height: 32, objectFit: 'contain' }}
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          ) : (
            <ClassIcon classId={classId} color={accent} size={28} />
          )}
        </div>
        <span
          style={{
            fontFamily: "'Raleway', sans-serif",
            fontWeight: 700,
            fontSize: '0.68rem',
            color: accent,
            letterSpacing: '0.15em',
            textTransform: 'uppercase',
            whiteSpace: 'pre-line',
            textAlign: 'center',
            lineHeight: 1.4,
          }}
        >
          {c.tag}
        </span>
      </div>

      {/* Title — all cards use Hand Built Matcha Bowl styling */}
      <h3
        style={{
          fontFamily: "'Raleway', sans-serif",
          fontWeight: 700,
          fontSize: 'clamp(1.3rem, 4vw, 1.7rem)',
          color: '#1a1208',
          textTransform: 'uppercase',
          letterSpacing: '0em',
          marginBottom: 8,
          textAlign: 'center',
          lineHeight: 1.1,
        }}
      >
        {c.title}
      </h3>

      {/* Description */}
      {c.description && (
        <p
          style={{
            fontFamily: "'Raleway', sans-serif",
            fontWeight: 700,
            fontSize: 'clamp(0.78rem, 1.5vw, 0.88rem)',
            color: '#9C7A3C',
            textAlign: 'center',
            marginBottom: 8,
            maxWidth: 320,
            lineHeight: 1.5,
          }}
        >
          {c.description}
        </p>
      )}

      {/* Location */}
      <div
        style={{
          fontFamily: "'Raleway', sans-serif",
          fontWeight: 700,
          fontSize: 'clamp(0.78rem, 1.5vw, 0.88rem)',
          color: '#1a1208',
          marginBottom: 10,
          textAlign: 'center',
        }}
      >
        {c.location}
      </div>

      {/* Card photo */}
      {cardImage && (
        <div
          style={{
            width: '100%',
            height: 140,
            borderRadius: 12,
            overflow: 'hidden',
            background: '#F9F2E9',
            marginBottom: 12,
            flexShrink: 0,
          }}
        >
          <img
            src={cardImage}
            alt={c.title}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              objectPosition: 'center',
            }}
            onError={(e) => {
              e.currentTarget.parentElement.style.display = 'none';
            }}
          />
        </div>
      )}

      {/* CTA button — filled pill matching site's Explore Classes / Sessions style */}
      <div style={{ marginTop: 'auto' }}>
        <button
          disabled={!active}
          onClick={(e) => {
            e.stopPropagation();
            if (active) {
              if (analytics)
                logEvent(analytics, 'select_item', {
                  item_name: c.title,
                  price: c.price,
                });
              onBook(c);
            }
          }}
          style={{
            fontFamily: "'Raleway', sans-serif",
            fontWeight: 700,
            fontSize: '1rem',
            background: accent,
            color: '#fff',
            border: 'none',
            borderRadius: 50,
            padding: '14px 32px',
            cursor: active ? 'pointer' : 'default',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            letterSpacing: '0.01em',
          }}
          onMouseEnter={(e) => {
            if (!active) return;
            e.currentTarget.style.transform = 'translateY(-3px)';
            e.currentTarget.style.boxShadow = `0 8px 24px ${accent}55`;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          {label}
        </button>
      </div>

      {/* Corner ornament */}
      <div
        style={{
          position: 'absolute',
          top: 14,
          right: 18,
          color: `${accent}66`,
          fontSize: '0.6rem',
          letterSpacing: 4,
        }}
      >
        ◦ ◦
      </div>

      {/* Progress counter */}
      <div
        style={{
          position: 'absolute',
          bottom: 14,
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          gap: 6,
        }}
      >
        {Array.from({ length: total }).map((_, dot) => (
          <div
            key={dot}
            style={{
              width: dot === index ? 16 : 5,
              height: 5,
              borderRadius: 3,
              background: dot === index ? accent : `${accent}44`,
              transition: 'width 0.3s',
            }}
          />
        ))}
      </div>
    </motion.div>
  );
}

// ── Main component ─────────────────────────────────────────────
export default function ClassGrid({ onBook }) {
  const ease = [0.22, 1, 0.36, 1];
  const viewport = { once: false, amount: 0.05 };
  const containerRef = useRef(null);

  const { cards, loading: cardsLoading } = useClassCards();
  const { defaults: templateDefaults } = useTemplateDefaults();
  const {
    sessionCardIds,
    soldOutCardIds,
    pastSessionCardIds,
    earliestSessionDate,
    loading: sessionsLoading,
  } = usePublicSessions();
  const loading = cardsLoading || sessionsLoading;

  const templateImageMap = useMemo(() => {
    const m = {};
    Object.entries(templateDefaults).forEach(([id, d]) => {
      if (d.defaultCardImageUrl) m[id] = d.defaultCardImageUrl;
    });
    return m;
  }, [templateDefaults]);

  const templateIconMap = useMemo(() => {
    const m = {};
    Object.entries(templateDefaults).forEach(([id, d]) => {
      if (d.defaultIconUrl) m[id] = d.defaultIconUrl;
    });
    return m;
  }, [templateDefaults]);

  // Older cards may not have `visibility` saved — fall back to checking the tag text
  const isPrivate = (c) =>
    c.visibility === 'Private' || (c.tag || '').includes('Private');

  // Sort priority:
  // 0 — active open sessions (bookable)
  // 1 — sold out sessions
  // 2 — past sessions only (previously ran)
  // 3 — no sessions (join the waitlist)
  // 4 — pottery party cards (inquiry / private)
  // 5 — wheel throwing cards (inquiry / private)
  // 6 — other private / inquiry cards
  const sortedCards = useMemo(() => {
    const rank = (c) => {
      if (sessionCardIds.has(c.id)) return 0;
      if (soldOutCardIds.has(c.id)) return 1;
      if (pastSessionCardIds.has(c.id)) return 2;
      if (!isPrivate(c)) return 3;
      if (c.templateId === 'pottery-party') return 4;
      if (c.templateId === 'wheel-throwing') return 5;
      return 6;
    };
    return [...cards].sort((a, b) => {
      const rankDiff = rank(a) - rank(b);
      if (rankDiff !== 0) return rankDiff;
      // Within the same rank, sort rank-0 cards by earliest upcoming session date
      if (rank(a) === 0) {
        const da = earliestSessionDate.get(a.id) || '9999';
        const db_ = earliestSessionDate.get(b.id) || '9999';
        return da.localeCompare(db_);
      }
      return 0;
    });
  }, [
    cards,
    sessionCardIds,
    soldOutCardIds,
    pastSessionCardIds,
    earliestSessionDate,
  ]);

  const N = sortedCards.length || 1;
  const isSingle = sortedCards.length === 1;

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end end'],
  });

  // (isPrivate moved above sortedCards)

  const buttonLabel = (c) => {
    if (isPrivate(c)) return 'Inquire';
    if (sessionCardIds.has(c.id)) return 'Reserve a Spot';
    if (soldOutCardIds.has(c.id)) return 'Sold Out — Join Waitlist';
    return 'Join the Waitlist';
  };

  // All cards are clickable — active sessions book, sold out / no sessions show waitlist
  const isActive = () => true;

  const NAV_H = 72; // px — matches nav height

  return (
    <div id="all-classes-section" style={{ paddingTop: 80 }}>
      {/* ── Animated headline ─────────────────────────────────── */}
      <div
        className="section-pad"
        style={{ marginBottom: 0, userSelect: 'none', lineHeight: 0.92 }}
      >
        <div style={{ maxWidth: 480, margin: '0 auto' }}>
          {/* CLASSES */}
          <motion.div
            initial={{ opacity: 0, x: 80, rotate: -14 }}
            whileInView={{ opacity: 1, x: 0, rotate: -9 }}
            viewport={viewport}
            transition={{ duration: 0.9, ease }}
            style={{
              fontFamily: "'Titan One', sans-serif",
              fontWeight: 400,
              fontSize: 'clamp(2.6rem, 5.2vw, 5rem)',
              color: '#1a1208',
              letterSpacing: '-0.03em',
              textTransform: 'uppercase',
              display: 'block',
              paddingLeft: '2%',
            }}
          >
            Classes
          </motion.div>

          {/* AND */}
          <motion.div
            initial={{ opacity: 0, x: 80, rotate: 16 }}
            whileInView={{ opacity: 1, x: 0, rotate: 9 }}
            viewport={viewport}
            transition={{ duration: 0.9, ease, delay: 0.12 }}
            style={{
              fontFamily: "'Titan One', sans-serif",
              fontWeight: 400,
              fontSize: 'clamp(2.6rem, 5.2vw, 5rem)',
              color: '#1a1208',
              letterSpacing: '-0.03em',
              textTransform: 'uppercase',
              display: 'block',
              paddingLeft: '35%',
              marginTop: '0.3em',
            }}
          >
            And
          </motion.div>

          {/* WORKSHOPS */}
          <motion.div
            initial={{ opacity: 0, x: 80, rotate: 5 }}
            whileInView={{ opacity: 1, x: 0, rotate: 1.5 }}
            viewport={viewport}
            transition={{ duration: 0.9, ease, delay: 0.24 }}
            style={{
              fontFamily: "'Titan One', sans-serif",
              fontWeight: 400,
              fontSize: 'clamp(2.6rem, 5.2vw, 5rem)',
              color: '#1a1208',
              letterSpacing: '-0.03em',
              textTransform: 'uppercase',
              display: 'block',
              marginTop: '-0.05em',
            }}
          >
            Workshops
          </motion.div>
        </div>
      </div>

      {/* ── Sticky scroll container ───────────────────────────── */}
      {/* Outer wrapper: provides the scroll budget (N segments × 85vh each) */}
      {/* Single card: no scroll budget needed, just display the card centered */}
      <div
        ref={containerRef}
        style={{
          position: 'relative',
          height: isSingle ? '85vh' : `${N * 85}vh`,
        }}
      >
        {/* Sticky inner: pins to viewport while scroll budget is consumed */}
        <div
          style={{
            position: 'sticky',
            top: NAV_H,
            height: `calc(100vh - ${NAV_H}px)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: isSingle ? 'visible' : 'hidden',
          }}
        >
          {/* Deck container: fixed size, cards stacked absolutely inside */}
          <div
            style={{
              position: 'relative',
              width: 'min(420px, 88vw)',
              height: isSingle ? 'min(540px, 78vh)' : 'min(540px, 78vh)',
            }}
          >
            {/* Depth layers behind the deck */}
            <div
              style={{
                position: 'absolute',
                inset: 0,
                transform: 'translateY(10px) scale(0.97)',
                borderRadius: 22,
                background: '#EDE6DC',
                border: '4px solid #DDD5C8',
                zIndex: 0,
              }}
            />
            <div
              style={{
                position: 'absolute',
                inset: 0,
                transform: 'translateY(5px) scale(0.985)',
                borderRadius: 22,
                background: '#F2EBE1',
                border: '4px solid #E0D9CF',
                zIndex: 0,
              }}
            />

            {/* Cards */}
            {loading
              ? null
              : sortedCards.map((c, i) => (
                  <StackCard
                    key={c.id}
                    c={c}
                    index={i}
                    total={N}
                    scrollYProgress={scrollYProgress}
                    active={isActive(c)}
                    label={buttonLabel(c)}
                    onBook={onBook}
                    cardImage={
                      c.useDefaultCardImage === false && c.cardImageUrl
                        ? c.cardImageUrl
                        : templateImageMap[c.templateId] || ''
                    }
                    cardIconUrl={
                      c.useTemplateImage === false && c.illustration
                        ? c.illustration
                        : templateIconMap[c.templateId] || ''
                    }
                  />
                ))}

            {/* "More classes coming soon" placeholder — always last in the deck */}
            {!loading && (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  zIndex: 0,
                  borderRadius: 22,
                  background: '#F9F2E9',
                  border: '4px solid #DDD8CC',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                }}
              >
                <span
                  style={{
                    fontFamily: "'Caveat', cursive",
                    fontSize: 'clamp(1.4rem, 3.5vw, 1.8rem)',
                    color: '#4A6FA5',
                    textAlign: 'center',
                    lineHeight: 1.35,
                  }}
                >
                  more classes
                  <br />
                  coming soon :)
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
